import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Youtube } from 'lucide-react';
import { SITE, getWhatsAppUrl } from '../lib/site';

export const Footer = () => {
  const whatsappUrl = getWhatsAppUrl();

  return (
    <footer id="contact" className="bg-slate-900 text-white pt-20 pb-8">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Placement note (subtle) */}
        <p className="text-xs text-gray-500 text-center mb-10 italic">
          *Placement Assistance Guarantee is offered on select programs. Talk to the Admissions Office for details.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* About */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_coursecraft-26/artifacts/drb8b1bq_MatchFrame%20White%20Logo.png"
              alt="MatchFrame Editing School Bangalore Logo"
              className="h-32 md:h-40 w-auto mb-6"
              style={{ maxWidth: '500px' }}
              loading="lazy"
            />
            <p className="text-gray-400 leading-relaxed mb-6">
              Bangalore&apos;s premier video editing institute. Transform your passion into a professional career.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/matchframe_blr/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@matchframe_editing_school_blr/videos"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube-link"
                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="w-10 h-10 bg-white/10 hover:bg-[#25D366] rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              >
                <svg viewBox="0 0 32 32" className="h-5 w-5 text-white fill-current" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825 0.737 5.607 2.137 8.048l-2.137 7.952 7.933-2.127c2.42 1.37 5.173 2.127 8.067 2.127 8.837 0 16-7.163 16-16s-7.163-16-16-16zM16 29.467c-2.482 0-4.908-0.646-7.07-1.87l-0.507-0.292-5.247 1.408 1.417-5.267-0.319-0.525c-1.331-2.178-2.037-4.679-2.037-7.253 0-7.51 6.11-13.62 13.62-13.62s13.62 6.11 13.62 13.62-6.11 13.62-13.62 13.62zM21.305 19.26c-0.346-0.174-2.049-1.007-2.366-1.123-0.316-0.117-0.547-0.174-0.776 0.174s-0.892 1.123-1.094 1.347c-0.201 0.231-0.403 0.26-0.748 0.087-0.346-0.174-1.461-0.539-2.785-1.722-1.031-0.922-1.727-2.061-1.929-2.407-0.201-0.346-0.022-0.533 0.152-0.705 0.156-0.155 0.346-0.403 0.518-0.605 0.174-0.201 0.231-0.346 0.346-0.576 0.117-0.231 0.058-0.433-0.028-0.605s-0.776-1.87-1.063-2.565c-0.28-0.672-0.56-0.58-0.776-0.591-0.201-0.008-0.431-0.010-0.661-0.010s-0.604 0.087-0.919 0.433c-0.316 0.346-1.206 1.179-1.206 2.873s1.235 3.333 1.406 3.561c0.174 0.231 2.441 3.732 5.913 5.234 0.827 0.358 1.472 0.572 1.975 0.733 0.831 0.263 1.586 0.226 2.184 0.138 0.666-0.099 2.049-0.838 2.335-1.647 0.289-0.808 0.289-1.501 0.201-1.647-0.087-0.143-0.316-0.231-0.662-0.403z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li><a href="/#courses" className="text-gray-400 hover:text-white transition-colors duration-300">Our Courses</a></li>
              <li><Link to="/hiring-partners" className="text-gray-400 hover:text-white transition-colors duration-300">For Hiring Partners</Link></li>
              <li><a href="/#testimonials" className="text-gray-400 hover:text-white transition-colors duration-300">Success Stories</a></li>
              <li><a href="/#faq" className="text-gray-400 hover:text-white transition-colors duration-300">FAQ</a></li>
              <li><a href="/#lead-form" className="text-gray-400 hover:text-white transition-colors duration-300">Book Consultation</a></li>
            </ul>
          </div>

          {/* Courses */}
          <div>
            <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">
              Programs
            </h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/courses/single-software-editing" className="text-gray-400 hover:text-white transition-colors duration-300">Single Software for Editing</Link></li>
              <li><Link to="/courses/master-ai-video-creation" className="text-gray-400 hover:text-white transition-colors duration-300">Master in AI Video Creation</Link></li>
              <li><Link to="/courses/master-digital-color-grading" className="text-gray-400 hover:text-white transition-colors duration-300">Master in Digital Color Grading</Link></li>
              <li><Link to="/courses/ai-integrated-video-editing" className="text-gray-400 hover:text-white transition-colors duration-300">AI Integrated Video Editing</Link></li>
              <li><Link to="/courses/master-in-film-editing" className="text-gray-400 hover:text-white transition-colors duration-300">Master in Film Editing</Link></li>
              <li><Link to="/courses/master-in-social-media-editing" className="text-gray-400 hover:text-white transition-colors duration-300">Master in Social Media Editing</Link></li>
              <li><Link to="/courses/advanced-diploma-film-video-editing" className="text-gray-400 hover:text-white transition-colors duration-300">Advanced Diploma in Film &amp; Video Editing</Link></li>
              <li><Link to="/courses/professional-diploma-film-video-editing" className="text-gray-400 hover:text-white transition-colors duration-300">Professional Diploma in Film &amp; Video Editing</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent">
              Contact Us
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-purple-400 flex-shrink-0 mt-1" />
                <p className="text-gray-400 text-sm leading-relaxed">
                  Unit #309, 3rd Floor, No. 77/A, Workafella,<br />
                  Jyothi Nivas College Road,<br />
                  Koramangala Industrial Layout, Koramangala,<br />
                  Bangalore, Karnataka 560095, India
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <a href={`tel:${SITE.phone}`} className="text-gray-400 hover:text-white transition-colors duration-300">
                  {SITE.phoneDisplay}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <a href={`mailto:${SITE.email}`} className="text-gray-400 hover:text-white transition-colors duration-300">
                  {SITE.email}
                </a>
              </div>
              <a
                href="https://maps.app.goo.gl/BK7JJQdSVe71Uu55A"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-full text-sm font-semibold transform hover:scale-105 transition-all duration-300"
              >
                Get Directions
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 mt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <p data-testid="footer-copyright">{SITE.copyrightLine}</p>
            <div className="flex gap-6">
              <a href="/privacy-policy" className="hover:text-white transition-colors duration-300">Privacy Policy</a>
              <a href="/terms-of-service" className="hover:text-white transition-colors duration-300">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
